import { filterByDocumentType, sortDocumentsDescending } from 'Utility/documentHandling';
import { take, uniq } from 'Utility/ramdaReplacement';

import type { RootState } from '../../../store';

export type TComorbidityDocumentType = SafeExtract<
  TDocumentType,
  'comorbidity' | 'hospitalization' | 'pregnancy' | 'contraception' | 'sickLeave'
>;

type TSecondaryDocumentType = SafeExtract<TDocumentType, 'medication'>;

export type TRelevantDocumentType = TComorbidityDocumentType | TSecondaryDocumentType;

export const sortAndTakeMany = <T extends TDocumentType>(
  type: T,
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[],
  number?: number,
  filter?: (doc: IDocumentWithTypeV2<T>) => boolean,
): IDocumentWithTypeV2<T>[] =>
  take(
    number ?? filterByDocumentType(documents, type).length,
    filterByDocumentType(documents, type)
      .filter(filter ?? (() => true))
      .sort((n1, n2) => sortDocumentsDescending(n1, n2)),
  );

export const filterEnded = <T extends SafeExtract<TDocumentType, 'comorbidity'>>(
  documents: IDocumentWithTypeV2<T>[],
): IDocumentWithTypeV2<T>[] => documents.filter(d => !d.endDate || !d.endDate[0]);

export const getMedicationNames = (documents: IDocumentWithTypeV2<TRelevantDocumentType>[]): string[] => {
  const meds = filterByDocumentType(documents, 'medication');
  const medsFiltered = meds.filter(m => !!m.medicationName);
  const medNames = uniq(medsFiltered.map(m => m.medicationName) as string[]);
  return medNames;
};

export const getDiagnosisCodes = (documents: IDocumentWithTypeV2<TRelevantDocumentType>[]): string[] => {
  const diagnosis = filterByDocumentType(documents, 'diagnosis');
  const diagnosisCodes = [];
  for (const dx of diagnosis) {
    if (dx.diagnosis) diagnosisCodes.push(dx.diagnosis);
  }
  return diagnosisCodes;
};

export const stateToHistoryProps = (state: RootState): Pick<IJWTBody, 'visitreason' | 'patientssn'> => {
  return {
    visitreason: state.session.data?.visitreason ?? '',
    patientssn: state.session.data?.patientssn ?? '',
  };
};
